'use client';
import React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Image from 'next/image';
import Link from 'next/link';
import api from '@/lib/api';
import { notify } from '@/lib/toast';
import { useAuthStore } from '@/store/auth.store';
import { useMessagesStore } from '@/store/messages.store';
import { trackSpecialistView } from '@/lib/analytics';
import { pluralReviews, formatSlotDate } from '@/components/specialists/StarRating';
import ServicesList from '@/components/specialists/ServicesList';
import BookingDrawer from '@/components/specialists/BookingDrawer';
import { sanitizeDescription } from '@/lib/sanitizeText';
import {
  Star, MapPin, Clock, Check, Heart, Briefcase, Globe, Phone,
  ShieldCheck, Share2, MessageSquare, Award, Zap, ChevronDown,
} from 'lucide-react';
import BackButton from '@/components/ui/BackButton';
import Breadcrumbs from '@/components/ui/Breadcrumbs';

import ContactsBlock from '@/components/specialists/ContactsBlock';

const getAvatarColor = (name: string) => {
  const colors = [
    'from-brand to-[#1a4a4e]',
    'from-violet-500 to-purple-700',
    'from-blue-500 to-indigo-700',
    'from-rose-500 to-pink-700',
    'from-amber-500 to-orange-600',
  ];
  return colors[(name?.charCodeAt(0) ?? 0) % colors.length];
};

const toLocalDate = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

const toLocalDateOffset = (ms: number) => {
  const n = new Date(Date.now() + ms);
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

// ─── RatingBar ──────────────────────────────────────────────────────────────
/**
 * Распределение оценок приходит с бэкенда (GROUP BY rating по всем отзывам).
 * Раньше считалось по массиву reviews, а он ограничен take: 10 — при большем
 * числе отзывов гистограмма показывала только последнюю десятку (ТЗ 6.3).
 */
function RatingBar({ distribution }: { distribution: Record<number, number> }) {
  const stars = [5, 4, 3, 2, 1];
  const dist: Record<number, number> = {
    5: distribution?.[5] ?? 0, 4: distribution?.[4] ?? 0, 3: distribution?.[3] ?? 0,
    2: distribution?.[2] ?? 0, 1: distribution?.[1] ?? 0,
  };
  const max = Math.max(...Object.values(dist), 1);
  return (
    <div className="space-y-1.5">
      {stars.map((s) => (
        <div key={s} className="flex items-center gap-2 text-xs">
          <span className="text-gray-500 w-2 text-right">{s}</span>
          <Star size={10} className="fill-yellow-400 text-yellow-400 flex-shrink-0" />
          <div className="flex-1 h-1.5 bg-bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-yellow-400 rounded-full transition-all" style={{ width: `${(dist[s] / max) * 100}%` }} />
          </div>
          <span className="text-gray-400 w-4 text-right">{dist[s]}</span>
        </div>
      ))}
    </div>
  );
}

// ─── LightboxGallery ────────────────────────────────────────────────────────
function LightboxGallery({ items, startIndex, onClose }: { items: any[]; startIndex: number; onClose: () => void }) {
  const [idx, setIdx] = useState(startIndex);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIdx((i) => Math.min(i + 1, items.length - 1));
      if (e.key === 'ArrowLeft') setIdx((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [items.length, onClose]);
  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center" onClick={onClose}>
      <div className="relative max-w-4xl max-h-[90vh] w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-2 right-2 z-10 w-9 h-9 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white text-lg transition-all border border-white/20">×</button>
        {idx > 0 && (
          <button onClick={() => setIdx((i) => i - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white text-xl transition-all border border-white/20">‹</button>
        )}
        {idx < items.length - 1 && (
          <button onClick={() => setIdx((i) => i + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white text-xl transition-all border border-white/20">›</button>
        )}
        <Image src={items[idx].imageUrl} alt="" width={1200} height={900} className="w-full h-full object-contain max-h-[80vh] rounded-xl" />
        <div className="text-center text-white/50 text-xs mt-3">{idx + 1} / {items.length}</div>
      </div>
    </div>
  );
}

// ─── ОСНОВНОЙ КОМПОНЕНТ ─────────────────────────────────────────────────────
export default function ProfileClient({ username, initialData }: { username: string; initialData: any }) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { isAuthenticated, user: authUser } = useAuthStore();
  const { setOpenWithUserId } = useMessagesStore();

  const handleChat = () => {
    if (specialist?.user?.id) setOpenWithUserId(specialist.user.id);
  };

  const [copied, setCopied] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);
  // Био раскрыто по умолчанию (ТЗ 6.2): для психолога, репетитора, юриста
  // это главный аргумент, прятать его за кликом неверно.
  const [showBio, setShowBio] = useState(true);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingSvcId, setBookingSvcId] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [lightbox, setLightbox] = useState<{ open: boolean; index: number }>({ open: false, index: 0 });
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportComment, setReportComment] = useState('');
  const reportMutation = useMutation({
    mutationFn: (data: { reason: string; comment?: string }) =>
      api.post('/reports', { targetType: 'specialist_card', targetId: specialist?.id, ...data }),
    onSuccess: () => {
      notify.success('Жалоба принята. Мы рассмотрим её в течение 24 часов');
      setReportOpen(false); setReportReason(''); setReportComment('');
    },
    onError: (e: any) => {
      const code = e?.response?.data?.error?.code;
      if (code === 'ALREADY_REPORTED') notify.error('Вы уже подавали жалобу на эту карточку');
      else notify.error('Не удалось отправить жалобу');
    },
  });

  const { data: specialist, isLoading, error } = useQuery({
    queryKey: ['specialist-by-username', username],
    queryFn: () => api.get(`/specialists/by-username/${username}`).then((r) => r.data.data),
    initialData: initialData ?? undefined,
    enabled: !!username,
  });

  useEffect(() => {
    if (specialist) trackSpecialistView(specialist.id);
  }, [specialist]);

  const { data: portfolio = [] } = useQuery({
    queryKey: ['portfolio', specialist?.id],
    queryFn: () => api.get(`/specialists/${specialist.id}/portfolio`).then((r) => r.data.data),
    enabled: !!specialist?.id,
  });

  const { data: favorites = [] } = useQuery({
    queryKey: ['favorites'],
    queryFn: () => api.get('/favorites').then((r) => r.data.data),
    enabled: isAuthenticated(),
  });

  const isFavorite = Array.isArray(favorites) && favorites.some(
    (f: any) => f.specialistId === specialist?.id || f.id === specialist?.id,
  );

  const favMutation = useMutation({
    mutationFn: () =>
      isFavorite
        ? api.delete(`/favorites/${specialist.id}`)
        : api.post('/favorites', { specialist_id: specialist.id }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['favorites'] }),
  });

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      await navigator.share({ title: `${specialist?.user?.firstName} ${specialist?.user?.lastName} — СвойПрофи`, url });
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Ближайший слот (первый доступный по любой услуге)
  const availDateFrom = useMemo(() => toLocalDate(), []);
  const nearestDateTo = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + 14);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);
  const { data: nearestData } = useQuery({
    queryKey: ['specialist-nearest-u', specialist?.id, availDateFrom, nearestDateTo],
    queryFn: () =>
      api
        .get(`/specialists/${specialist.id}/availability`, {
          params: { date_from: availDateFrom, date_to: nearestDateTo },
        })
        .then((r) => r.data.data),
    enabled: !!specialist?.id,
  });
  const nearestSlot = useMemo(() => {
    if (!Array.isArray(nearestData) || nearestData.length === 0) return null;
    const first = nearestData[0];
    if (!first?.startAt) return null;
    const tz = specialist?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const d = new Date(first.startAt);
    const slotDateStr = d.toLocaleDateString('sv-SE', { timeZone: tz });
    const isToday = slotDateStr === toLocalDate();
    const isTomorrow = slotDateStr === toLocalDateOffset(86400000);
    const label = isToday ? 'сегодня' : isTomorrow ? 'завтра' : formatSlotDate(slotDateStr).toLowerCase();
    const time = d.toLocaleTimeString('ru-RU', { timeZone: tz, hour: '2-digit', minute: '2-digit' });
    return `${label} в ${time}`;
  }, [nearestData, specialist?.timezone]);

  // ── загрузка ──
  if (isLoading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-subtle">
        <div className="space-y-4 w-full max-w-2xl px-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-2xl animate-pulse" />
          ))}
        </div>
      </div>
    );

  if (error || !specialist)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-bg-subtle">
        <div className="text-7xl font-bold text-gray-200">404</div>
        <div className="text-xl text-text-secondary">Профиль @{username} не найден</div>
        <Link href="/marketplace" className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold" style={{ backgroundColor: 'var(--color-brand)' }}>
          Найти специалиста
        </Link>
      </div>
    );

  const {
    user: sp, bio, rating, reviewsCount, services, experienceYears,
    isAvailable, planBadge, workFormat, workPhone, website, telegramUsername, maxUsername, hasContacts,
  } = specialist;

  const categoryName =
    specialist.category?.name ??
    services?.find((s: any) => s.category?.name)?.category?.name ??
    null;

  const avatarColor = getAvatarColor(`${sp?.firstName}${sp?.lastName}`);
  const minPrice = services?.length ? Math.min(...services.map((s: any) => Number(s.price))) : null;
  const completedCount = specialist._count?.bookings ?? 0;
  const reviews = specialist.reviews ?? [];
  const displayedReviews = showAllReviews ? reviews : reviews.slice(0, 3);

  // workFormat → иконка для шапки
  const workFormatLabel =
    workFormat === 'online' ? '💻 Онлайн'
    : workFormat === 'mobile' ? '🚗 Выезд к клиенту'
    : specialist?.city ? null : null;

  return (
    <div className="min-h-screen bg-bg-subtle">

      {/* ───── HERO ───── */}
      <div className="bg-bg-surface border-b border-border-subtle">
        <div className="max-w-3xl mx-auto px-4 pt-3 pb-3 sm:pt-6 sm:pb-6">
          <BackButton label="Назад" />
          <Breadcrumbs crumbs={[
            { label: 'Главная', href: '/' },
            { label: 'Специалисты', href: '/' },
            { label: sp?.firstName ? `${sp.firstName} ${sp.lastName ?? ''}`.trim() : username },
          ]} />
          <div className="flex flex-col sm:flex-row items-start gap-5">

            {/* Аватар */}
            <div className="relative flex-shrink-0 self-center sm:self-start">
              {sp?.avatarUrl ? (
                <Image
                  src={sp.avatarUrl}
                  alt={`${sp.firstName} ${sp.lastName}`}
                  width={96} height={96}
                  className="w-16 h-16 sm:w-24 sm:h-24 rounded-2xl object-cover shadow-sm border border-border-subtle"
                />
              ) : (
                <div className={`w-16 h-16 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br ${avatarColor} flex items-center justify-center text-white font-bold text-2xl sm:text-3xl shadow-sm`}>
                  {sp?.firstName?.[0]}{sp?.lastName?.[0]}
                </div>
              )}
              <span className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${isAvailable ? 'bg-green-400' : 'bg-gray-300'}`} />
            </div>

            {/* Инфо */}
            <div className="flex-1 min-w-0">

              {/* Строка 1: имя + бейджи + кнопки */}
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h1 className="text-xl sm:text-2xl font-bold text-text-primary">
                  {sp?.displayName || `${sp?.firstName} ${sp?.lastName}`}
                </h1>
                {(categoryName || sp?.city) && (
                  <p className="sr-only">
                    {[categoryName, sp?.city].filter(Boolean).join(', ')}
                  </p>
                )}
                {sp?.isVerified && <ShieldCheck size={18} className="text-brand" />}
                {planBadge && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-brand/10 text-brand font-semibold">{planBadge}</span>
                )}
                <div className="flex gap-1.5 ml-auto">
                  {isAuthenticated() && authUser?.id !== specialist?.user?.id && (
                    <button
                      onClick={handleChat}
                      className="p-1.5 rounded-xl bg-bg-subtle hover:bg-bg-muted text-gray-500 hover:text-brand transition-all border border-border"
                      title="Написать сообщение"
                    >
                      <MessageSquare size={14} />
                    </button>
                  )}
                  <button
                    onClick={handleShare}
                    className="p-1.5 rounded-xl bg-bg-subtle hover:bg-bg-muted text-gray-500 transition-all border border-border"
                    title="Поделиться"
                  >
                    <Share2 size={14} />
                  </button>
                  {isAuthenticated() && (
                    <button
                      onClick={() => favMutation.mutate()}
                      className={`p-1.5 rounded-xl transition-all border ${isFavorite ? 'bg-rose-500 border-rose-500 text-white' : 'bg-bg-subtle border-border hover:border-brand text-gray-500'}`}
                      title="В избранное"
                    >
                      <Heart size={14} className={isFavorite ? 'fill-white' : ''} />
                    </button>
                  )}
                  {isAuthenticated() && authUser?.id !== specialist?.user?.id && (
                    <button
                      onClick={() => setReportOpen(true)}
                      className="p-1.5 rounded-xl bg-bg-subtle hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all border border-border hover:border-red-300"
                      title="Пожаловаться"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="currentColor"/>
                        <line x1="12" y1="9" x2="12" y2="13" stroke="#e74c3c" strokeWidth="2.5"/>
                        <line x1="12" y1="17" x2="12.01" y2="17" stroke="#e74c3c" strokeWidth="2.5"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Строка 2: категория + доступность */}
              <div className="flex flex-wrap items-center gap-2 mb-1">
                {categoryName && <span className="text-sm text-brand font-medium">{categoryName}</span>}
                {nearestSlot && (
                  <div className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 border border-green-100 rounded-lg px-2 py-0.5">
                    <Clock size={10} />
                    <span>Свободен: {nearestSlot}</span>
                  </div>
                )}
              </div>

              {/* Рейтинг и город — раньше лежали под «Подробнее», то есть три
                  сильнейших триггера доверия были не видны сразу (ТЗ 6.1) */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2">
                {reviewsCount > 0 && (
                  <span className="inline-flex items-center gap-1 text-sm">
                    <Star size={13} className="fill-yellow-400 text-yellow-400" />
                    <span className="font-bold text-text-primary">
                      {parseFloat(rating).toFixed(1).replace('.', ',')}
                    </span>
                    <span className="text-gray-400 text-xs">
                      · {reviewsCount} {pluralReviews(reviewsCount)}
                    </span>
                  </span>
                )}
                {specialist?.city && (
                  <span className="inline-flex items-center gap-1 text-sm text-gray-500">
                    <MapPin size={13} className="text-brand" />{specialist.city}
                  </span>
                )}
              </div>

              {/* CTA записи — открывает ту же панель, что и клик по услуге */}
              {(services?.length ?? 0) > 0 && (
                <button
                  onClick={() => { setBookingSvcId(null); setBookingOpen(true); }}
                  className="w-full sm:w-auto sm:px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 mb-1"
                  style={{ backgroundColor: 'var(--color-brand)' }}
                >
                  Записаться
                </button>
              )}

              {/* Подробнее */}
              <button
                onClick={() => setShowDetails(v => !v)}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-brand transition-colors mt-0.5"
              >
                {showDetails ? 'Скрыть' : 'Подробнее'}
                <ChevronDown size={12} className={`transition-transform duration-200 ${showDetails ? 'rotate-180' : ''}`} />
              </button>

              {/* Раскрываемый блок */}
              {showDetails && (
                <div className="mt-3 space-y-2">
                  {/* Город + формат */}
                  <div className="flex flex-wrap items-center gap-2">
                    {specialist?.city && (
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <MapPin size={11} className="text-brand" />{specialist.city}
                      </span>
                    )}
                    {workFormatLabel && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-brand/10 text-brand border border-brand/20">{workFormatLabel}</span>
                    )}
                    {workFormat === 'online' && !workFormatLabel && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-brand/10 text-brand border border-brand/20">💻 Онлайн</span>
                    )}
                    {workFormat === 'mobile' && !workFormatLabel && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-brand/10 text-brand border border-brand/20">🚗 Выезд</span>
                    )}
                  </div>
                  {/* Рейтинг + опыт + выполнено */}
                  <div className="flex flex-wrap items-center gap-3">
                    {reviewsCount > 0 && (
                      <div className="flex items-center gap-1">
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map((i) => (
                            <Star key={i} size={11} className={i <= Math.round(parseFloat(rating)) ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-200 text-gray-200'} />
                          ))}
                        </div>
                        <span className="text-text-primary font-bold text-xs">{parseFloat(rating).toFixed(1)}</span>
                        <span className="text-gray-400 text-xs">{reviewsCount} {pluralReviews(reviewsCount)}</span>
                      </div>
                    )}
                    {completedCount > 0 && (
                      <span className="flex items-center gap-1 text-gray-500 text-xs">
                        <Check size={11} className="text-green-500" />{completedCount} выполнено
                      </span>
                    )}
                    {experienceYears > 0 && (
                      <span className="flex items-center gap-1 text-gray-500 text-xs">
                        <Briefcase size={11} className="text-brand" />Опыт {experienceYears} {experienceYears === 1 ? 'год' : experienceYears < 5 ? 'года' : 'лет'}
                      </span>
                    )}
                  </div>
                  {/* Контакты */}
                  <ContactsBlock
                    specialistId={specialist.id}
                    hasContacts={hasContacts}
                  />
                </div>
              )}

            </div>


          </div>
        </div>
      </div>

      <BookingDrawer
        open={bookingOpen}
        onClose={() => setBookingOpen(false)}
        specialistId={specialist.id}
        specialist={specialist}
        services={services ?? []}
        initialServiceId={bookingSvcId}
      />

      {/* ───── КОНТЕНТ ───── */}
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">

        {/* Услуги с аккордеоном */}
        {(services?.length ?? 0) > 0 && (
          <div data-accordion className="bg-bg-surface rounded-2xl shadow-sm border border-border-subtle overflow-hidden">
            <div className="px-5 pt-5 pb-3">
              <h2 className="font-semibold text-text-primary text-base">Услуги и запись</h2>
              {minPrice !== null && (
                <p className="text-xs text-gray-400 mt-0.5">от {minPrice.toLocaleString()} ₽ · нажмите на услугу чтобы записаться</p>
              )}
            </div>
            <ServicesList
              services={services}
              onServiceClick={(id) => { setBookingSvcId(id); setBookingOpen(true); }}
            />
          </div>
        )}

        {/* О профи */}
        <div id="bio-section" className="bg-bg-surface rounded-2xl shadow-sm border border-border-subtle overflow-hidden">
          <button
            onClick={() => setShowBio(v => !v)}
            className="w-full flex items-center justify-between px-5 py-4 text-left"
          >
            <h2 className="font-semibold text-text-primary text-base">О профи</h2>
            <ChevronDown size={16} className={`text-gray-400 transition-transform duration-200 ${showBio ? 'rotate-180' : ''}`} />
          </button>
          {showBio && (
            <div className="px-5 pb-5">
              {bio ? (
                <>
                  <p
                    className={`text-text-secondary leading-relaxed text-sm whitespace-pre-line ${bioExpanded ? 'mb-2' : 'mb-2 line-clamp-4'}`}
                  >
                    {sanitizeDescription(bio)}
                  </p>
                  {/* Кнопка только для длинного текста: ~4 строки ≈ 320 символов */}
                  {sanitizeDescription(bio).length > 320 && (
                    <button
                      onClick={() => setBioExpanded(v => !v)}
                      className="text-sm text-brand font-medium hover:underline mb-4"
                    >
                      {bioExpanded ? 'Свернуть' : 'Читать далее'}
                    </button>
                  )}
                  <div className="mb-2" />
                </>
              ) : (
                <p className="text-gray-300 text-sm mb-4 italic">Профи пока не заполнил раздел «О себе»</p>
              )}
              {(experienceYears > 0 || completedCount > 0 || reviewsCount > 0 || specialist?.autoConfirm) && (
                <div className="grid grid-cols-2 gap-3">
                  {experienceYears > 0 && (
                    <div className="flex items-center gap-2 p-3 bg-bg-subtle rounded-xl">
                      <Briefcase size={16} className="text-brand" />
                      <div>
                        <div className="text-xs text-gray-400">Опыт</div>
                        <div className="text-sm font-medium text-text-primary">
                          {experienceYears} {experienceYears === 1 ? 'год' : experienceYears < 5 ? 'года' : 'лет'}
                        </div>
                      </div>
                    </div>
                  )}
                  {completedCount > 0 && (
                    <div className="flex items-center gap-2 p-3 bg-bg-subtle rounded-xl">
                      <Award size={16} className="text-brand" />
                      <div>
                        <div className="text-xs text-gray-400">Выполнено</div>
                        <div className="text-sm font-medium text-text-primary">{completedCount} заказов</div>
                      </div>
                    </div>
                  )}
                  {reviewsCount > 0 && (
                    <div className="flex items-center gap-2 p-3 bg-bg-subtle rounded-xl">
                      <Star size={16} className="text-yellow-400 fill-yellow-400" />
                      <div>
                        <div className="text-xs text-gray-400">Рейтинг</div>
                        <div className="text-sm font-medium text-text-primary">{parseFloat(rating).toFixed(1)} из 5</div>
                      </div>
                    </div>
                  )}
                  {specialist?.autoConfirm && (
                    <div className="flex items-center gap-2 p-3 bg-green-50 rounded-xl">
                      <Zap size={16} className="text-green-500" />
                      <div>
                        <div className="text-xs text-gray-400">Подтверждение</div>
                        <div className="text-sm font-medium text-text-primary">Автоматически</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Портфолио */}
        {(portfolio as any[]).length > 0 && (
          <div className="bg-bg-surface rounded-2xl p-5 shadow-sm border border-border-subtle">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-text-primary text-base">Портфолио</h2>
              <span className="text-xs text-gray-400">{(portfolio as any[]).length} работ</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {(portfolio as any[]).map((item: any, i: number) => (
                <button
                  key={item.id ?? i}
                  onClick={() => setLightbox({ open: true, index: i })}
                  className={`relative rounded-xl overflow-hidden group bg-bg-muted ${i % 5 === 0 ? 'row-span-2' : ''}`}
                  style={{ aspectRatio: i % 5 === 0 ? '1/2' : '1/1' }}
                >
                  <Image
                    src={item.imageUrl} alt={item.title || ''}
                    fill className="object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity w-8 h-8 bg-bg-surface/90 rounded-full flex items-center justify-center text-sm">🔍</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Отзывы */}
        {reviewsCount > 0 && (
          <div className="bg-bg-surface rounded-2xl p-5 shadow-sm border border-border-subtle">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-text-primary text-base">Отзывы</h2>
              <div className="flex items-center gap-1 text-sm">
                <Star size={14} className="fill-yellow-400 text-yellow-400" />
                <span className="font-bold text-text-primary">{parseFloat(rating).toFixed(1)}</span>
                <span className="text-gray-400 text-xs">({reviewsCount})</span>
              </div>
            </div>
            {reviews.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-200 text-3xl mb-2">⭐</div>
                <p className="text-gray-400 text-sm">Пока нет отзывов</p>
                <p className="text-gray-300 text-xs mt-1">Запишитесь и станьте первым!</p>
              </div>
            ) : (
              <>
                {/* До 10 отзывов гистограмма — это пять строк ради «все пятёрки»:
                    половина экрана и ноль информации. Тогда показываем строку (ТЗ 6.3). */}
                {reviewsCount >= 10 ? (
                  <div className="mb-5 p-4 bg-bg-subtle rounded-xl">
                    <RatingBar distribution={specialist?.ratingDistribution ?? {}} />
                  </div>
                ) : (
                  <div className="mb-5 text-sm text-gray-500">
                    <span className="font-bold text-text-primary">
                      {parseFloat(rating).toFixed(1).replace('.', ',')}
                    </span>
                    {' · '}{reviewsCount} {pluralReviews(reviewsCount)}
                  </div>
                )}
                <div className="space-y-4">
                  {displayedReviews.map((r: any) => (
                    <div key={r.id} className="pb-4 border-b border-gray-50 last:border-0 last:pb-0">
                      <div className="flex items-start gap-3">
                        {r.client?.avatarUrl ? (
                          <Image src={r.client.avatarUrl} alt="" width={36} height={36} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-bg-muted flex items-center justify-center flex-shrink-0 text-gray-400 text-sm font-medium">
                            {r.client?.firstName?.[0]}{r.client?.lastName?.[0]}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="text-sm font-medium text-text-primary">{r.client?.firstName} {r.client?.lastName}</span>
                            <div className="flex gap-0.5 flex-shrink-0">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <Star key={i} size={11} className={i < r.rating ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-100 text-gray-100'} />
                              ))}
                            </div>
                          </div>
                          {r.comment && (
                            <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-line">
                              {sanitizeDescription(r.comment)}
                            </p>
                          )}
                          {r.createdAt && (
                            <div className="text-xs text-gray-300 mt-1">
                              {new Date(r.createdAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {reviews.length > 3 && (
                  <button
                    onClick={() => setShowAllReviews(!showAllReviews)}
                    className="mt-4 w-full py-2.5 rounded-xl border border-border text-sm text-gray-500 hover:bg-bg-subtle transition-all flex items-center justify-center gap-1"
                  >
                    <ChevronDown size={14} className={`transition-transform ${showAllReviews ? 'rotate-180' : ''}`} />
                    {showAllReviews ? 'Скрыть' : `Показать все ${reviewsCount} отзыва`}
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* CTA — написать */}
        <div className="bg-bg-surface rounded-2xl p-5 shadow-sm border border-border-subtle flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-text-primary text-sm">Остались вопросы?</p>
            <p className="text-xs text-gray-400 mt-0.5">Напишите специалисту напрямую — уточните детали перед записью</p>
          </div>
          <button
            onClick={handleChat}
            className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-brand text-brand text-sm font-semibold hover:bg-brand/5 transition-all"
          >
            <MessageSquare size={15} /> Написать специалисту
          </button>
        </div>

      </div>

      {/* ───── STICKY CTA мобиль ───── */}


      {/* Lightbox */}
      {lightbox.open && (
        <LightboxGallery
          items={portfolio as any[]}
          startIndex={lightbox.index}
          onClose={() => setLightbox({ open: false, index: 0 })}
        />
      )}
      {reportOpen && (
        <div
          style={{ position:'fixed', inset:0, zIndex:500, backgroundColor:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}
          onClick={() => setReportOpen(false)}
        >
          <div style={{ backgroundColor:'var(--color-bg-surface)', borderRadius:'14px', padding:'20px', width:'100%', maxWidth:'360px', boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin:'0 0 4px', fontSize:'15px', fontWeight:700, color:'var(--color-text-primary)' }}>Пожаловаться на профиль</h3>
            <p style={{ margin:'0 0 14px', fontSize:'12px', color:'var(--color-text-muted)' }}>{specialist?.user?.firstName} {specialist?.user?.lastName}</p>
            <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginBottom:'12px' }}>
              {([
                { value:'fake_portfolio', label:'📷 Чужие работы / фейковое портфолио' },
                { value:'price_mismatch', label:'💰 Цены не соответствуют реальности' },
                { value:'false_info',     label:'📝 Ложная информация' },
                { value:'inappropriate',  label:'🔞 Неприемлемый контент' },
                { value:'fake_profile',   label:'👻 Фейковый профиль' },
                { value:'no_show',        label:'🚫 Не явился / игнорирует' },
                { value:'illegal',        label:'🚨 Незаконные услуги' },
                { value:'other',          label:'❓ Другое' },
              ] as const).map(({ value, label }) => (
                <label key={value} style={{ display:'flex', alignItems:'center', gap:'8px', cursor:'pointer', fontSize:'13px', color:'var(--color-text-primary)' }}>
                  <input type="radio" name="profile-report-reason" value={value} checked={reportReason === value} onChange={() => setReportReason(value)} style={{ accentColor:'var(--color-brand)' }} />
                  {label}
                </label>
              ))}
            </div>
            <textarea
              value={reportComment}
              onChange={e => setReportComment(e.target.value)}
              placeholder="Комментарий (необязательно)"
              rows={2}
              maxLength={500}
              style={{ width:'100%', resize:'none', border:'1px solid var(--color-border-input)', borderRadius:'8px', padding:'8px', fontSize:'13px', fontFamily:'inherit', backgroundColor:'var(--color-bg-subtle)', color:'var(--color-text-primary)', boxSizing:'border-box', marginBottom:'14px' }}
            />
            <div style={{ display:'flex', gap:'8px' }}>
              <button
                onClick={() => { setReportOpen(false); setReportReason(''); setReportComment(''); }}
                style={{ flex:1, padding:'9px', borderRadius:'8px', border:'1px solid var(--color-border)', background:'none', cursor:'pointer', fontSize:'13px', color:'var(--color-text-secondary)' }}
              >Отмена</button>
              <button
                onClick={() => reportMutation.mutate({ reason: reportReason, comment: reportComment || undefined })}
                disabled={!reportReason || reportMutation.isPending}
                style={{ flex:1, padding:'9px', borderRadius:'8px', border:'none', backgroundColor: reportReason ? '#e74c3c' : 'var(--color-border)', color:'#fff', cursor: reportReason ? 'pointer' : 'not-allowed', fontSize:'13px', fontWeight:600 }}
              >{reportMutation.isPending ? 'Отправка...' : 'Отправить'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
